import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { API, ClientDTO, UserDTO, UserFullDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UserService } from 'src/app/services/user.service';
import { UtilService } from 'src/app/services/util.service';
import { AdminUserEditModalComponent } from './admin-user-edit-modal/admin-user-edit-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { AdminUserSetRestoreCodeComponent } from './admin-user-set-restore-code/admin-user-set-restore-code.component';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig, FilterPanelComponent } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';

type SplitButtonAction = 'edit' | 'restore';

export interface RestoreUser {
  user?: UserFullDTO;
  restoreCode?: string;
}

@Component({
  selector: 'sb-page-admin-user-management',
  templateUrl: './admin-user-management.component.html',
  styleUrls: ['./admin-user-management.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUserManagementComponent extends RequestClass {
  public readonly userStatus = inject(UserService);
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  protected readonly api = inject(API);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // global variables
  restoreUser: RestoreUser = null;
  url: string | undefined = undefined;
  restoreUrl: string | undefined = undefined;

  // table wrapper variables
  users: UserFullDTO[] = [];

  // filter variables
  blocked: boolean | null = null;
  filteredClients: ClientDTO[] = null;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const clientIds = this.filteredClients?.length > 0 ? this.filteredClients.map(client => client.id) : null;
    const sub = this.api
      ._admin_User_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          blocked: this.blocked,
          isForDevice: clientIds != null ? false : null,
          memberOfClient_id: clientIds,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (users: UserFullDTO[]) => {
          this.users = this.buildSplitButtons(users);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  addUser() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: AdminUserEditModalComponent, data: {} },
        title: this.translate.instant('lang.adminUserEdit.createUser'),
        icon: 'plus',
        // modalOptions: {size: 'sm'}
      })
      .then(() => {
        this.requestDebounced();
      })
      .catch(() => {
        this.requestDebounced();
      });
  }

  editUser(pUser: UserDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: AdminUserEditModalComponent, data: { user: pUser } },
        title: this.translate.instant('lang.adminUserEdit.editUser'),
        icon: 'edit',
        // modalOptions: {size: 'sm'}
      })
      .then(() => {
        this.requestDebounced();
      })
      .catch(() => {
        this.requestDebounced();
      });
  }

  setRestoreCode(pUser: UserFullDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: AdminUserSetRestoreCodeComponent, data: { user: pUser } },
        title: this.translate.instant('lang.adminUserEdit.setRestoreCodeForUser', { userName: pUser.name }),
        icon: 'edit',
        // modalOptions: {size: 'sm'}
      })
      .then((data: RestoreUser) => {
        if (data) {
          this.url = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
          this.restoreUser = data;
          this.restoreUrl = this.url + '/passwordReset?username=' + data.user.username + '&token=' + data.restoreCode;
          this.restoreUrl = encodeURI(this.restoreUrl);
          this.modal.modal(
            {
              title: this.translate.instant('lang.globals.restoreCodeSetSuccessfully'),
              modalOptions: { windowClass: 'M' },
            },
            {
              templateId: 'copyRestoreLinkModal',
            }
          );
        }
      })
      .catch(() => {});
  }

  copyLinkToClipboard() {
    navigator.clipboard
      .writeText(this.restoreUrl)
      .then(() => {
        this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful'));
      })
      .catch(() => {
        this.toasterService.toast(ToastType.ERROR, this.translate.instant('lang.globals.error'));
      });
  }

  finish() {
    try {
      this.modal.close('copyRestoreLinkModal');
    } catch (ex) {}
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'edit',
        action: 'edit',
        tooltip: 'lang.globals.edit',
      });
      item.sbButtons.push({
        icon: 'key',
        action: 'restore',
        tooltip: 'lang.globals.setRestoreCode',
      });
    });
    return result;
  }

  resolveSplitButtonAction(user: UserFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editUser(user);
        break;
      case 'restore':
        this.setRestoreCode(user);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.blocked = filterMap.get('blocked')?.[0];
    this.filteredClients = filterMap.get('clients');
  }
}
